import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { PenSquare, SearchX, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { useSearchInsights } from "../../hooks/useArticles";
import { useDateFormat } from "../../hooks/useDateFormat";
import type { SearchInsightQuery } from "../../types/articles";

interface SearchInsightsCardProps {
  organizationIds?: string[];
}

const PERIODS = [7, 30, 90] as const;

const percent = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

// A query as an article title: searches are stored lowercased
const asTitle = (query: string) => query.charAt(0).toUpperCase() + query.slice(1);

function Stat({ label, value, detail }: { label: string; value: number; detail?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold text-foreground">{value.toLocaleString()}</p>
      {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

function QueryList({ title, icon, queries, empty, renderAction }: {
  title: string;
  icon: ReactNode;
  queries: SearchInsightQuery[];
  empty: string;
  renderAction?: (query: SearchInsightQuery) => ReactNode;
}) {
  const { formatDateOnly } = useDateFormat();
  return (
    <div className="min-w-0">
      <h4 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
        {icon}
        {title}
      </h4>
      {queries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border max-h-72 overflow-y-auto">
          {queries.map((q) => (
            <li key={q.query} className="flex items-center gap-2 py-1.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-foreground" title={q.query}>{q.query}</span>
                <span className="block text-xs text-muted-foreground">
                  {q.searches.toLocaleString()} {q.searches === 1 ? "search" : "searches"}
                  {q.averageResults > 0 && ` · ${q.clicks} opened a result`}
                  {` · last ${formatDateOnly(q.lastSearchedAt)}`}
                </span>
              </span>
              {renderAction?.(q)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * What customers search for in the customer portal's knowledge base: how many searches, how many found nothing and
 * how many led to an article, the most searched queries, and the queries that found nothing - each with a way to
 * start the article that would answer it. Searches are kept for 90 days.
 */
export function SearchInsightsCard({ organizationIds }: SearchInsightsCardProps) {
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30);
  const { data, isLoading, isError, isPlaceholderData } = useSearchInsights(organizationIds, days);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
              Search Insights
            </CardTitle>
            <CardDescription>
              What customers search for in the help center, and what they don't find.
            </CardDescription>
          </div>
          <div className="flex rounded-md border p-0.5" role="group" aria-label="Period">
            {PERIODS.map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => setDays(period)}
                aria-pressed={days === period}
                className={`px-2.5 py-1 text-xs rounded ${days === period ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {period} days
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className={`space-y-4 transition-opacity ${isPlaceholderData ? "opacity-60" : ""}`}>
        {isLoading ? (
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : isError || !data ? (
          <p className="text-sm text-destructive">Search insights couldn't be loaded.</p>
        ) : data.totalSearches === 0 ? (
          <p className="text-sm text-muted-foreground">No searches in the last {data.days} days yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Stat label="Searches" value={data.totalSearches} />
              <Stat
                label="Found nothing"
                value={data.zeroResultSearches}
                detail={`${percent(data.zeroResultSearches, data.totalSearches)}% of searches`}
              />
              <Stat
                label="Opened a result"
                value={data.searchesWithClicks}
                detail={`${percent(data.searchesWithClicks, data.searchesWithResults)}% of searches with results`}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <QueryList
                title="Top searches"
                icon={<TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                queries={data.topQueries}
                empty="No searches yet."
              />
              <QueryList
                title="Searches with no results"
                icon={<SearchX className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                queries={data.zeroResultQueries}
                empty="Every search found something."
                renderAction={(q) => (
                  <Link
                    to="/helpdesk/knowledge-base/new"
                    search={{ title: asTitle(q.query) }}
                    className="flex-shrink-0"
                    aria-label={`Write an article for "${q.query}"`}
                  >
                    <Badge variant="outline" className="gap-1 cursor-pointer hover:bg-accent">
                      <PenSquare className="h-3 w-3" aria-hidden="true" />
                      Write article
                    </Badge>
                  </Link>
                )}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
